import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { adminApi, imgUrl } from "../../lib/api";

export const useCrudList = (resource) => {
  const [items, setItems] = useState([]);
  const reload = () => adminApi.get(`/${resource}`).then((r) => setItems(r.items));
  useEffect(() => { reload().catch(() => {}); }, [resource]); // eslint-disable-line
  const remove = async (id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    await adminApi.delete(`/${resource}/${id}`);
    reload();
  };
  return { items, reload, remove };
};

export const ListHeader = ({ title, newTo, testId }) => (
  <div className="flex items-center justify-between mb-6">
    <h1 className="text-xl font-bold text-zinc-900">{title}</h1>
    {newTo && (
      <Link to={newTo} data-testid={testId}
        className="inline-flex items-center gap-1.5 bg-zinc-900 text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-zinc-700 transition-colors duration-150">
        <Plus className="w-4 h-4" /> New
      </Link>
    )}
  </div>
);

export const RowActions = ({ editTo, onDelete, testId }) => (
  <div className="flex gap-2 justify-end">
    {editTo && (
      <Link to={editTo} data-testid={`${testId}-edit`} className="p-1.5 text-zinc-500 hover:text-zinc-900" aria-label="Edit">
        <Pencil className="w-4 h-4" />
      </Link>
    )}
    <button onClick={onDelete} data-testid={`${testId}-delete`} className="p-1.5 text-zinc-500 hover:text-red-600" aria-label="Delete">
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
);

export default function AdminMagazines() {
  const { items, remove } = useCrudList("magazines");
  return (
    <div data-testid="admin-magazines-page">
      <ListHeader title="Magazines" newTo="/apadmin/magazines/new" testId="new-magazine-btn" />
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="magazines-table">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-zinc-200">
              <th className="px-4 py-3">Cover</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Frequency</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Featured</th><th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id} className="border-b border-zinc-100 last:border-0" data-testid={`magazine-row-${m.slug}`}>
                <td className="px-4 py-2">{m.cover_image && <img src={imgUrl(m.cover_image)} alt="" className="w-8 h-11 object-cover rounded-sm" />}</td>
                <td className="px-4 py-3 font-medium text-zinc-900">{m.title}<div className="text-xs text-zinc-400 font-normal">/{m.slug}</div></td>
                <td className="px-4 py-3 text-zinc-600">{m.category}</td>
                <td className="px-4 py-3 text-zinc-600">{m.frequency}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${m.status === "published" ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-600"}`}>{m.status}</span>
                </td>
                <td className="px-4 py-3 text-zinc-600">{m.featured ? "Yes" : "—"}</td>
                <td className="px-4 py-3"><RowActions editTo={`/apadmin/magazines/${m.id}/edit`} onDelete={() => remove(m.id)} testId={`magazine-${m.slug}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!items.length && <p className="text-sm text-zinc-500 text-center py-10">No magazines yet.</p>}
      </div>
    </div>
  );
}
